import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Tooltip from '../components/Tooltip';
import Footer from '../components/Footer';
import API_BASE_URL from '../apiConfig'; // <--- 1. Dynamic Import
import '../App.css'; 

// --- 2. Dynamic URL ---
const API_URL = `${API_BASE_URL}/assessment-result`;

function AssessmentResultPage() {
  const { linkToken } = useParams(); 
  const { token } = useAuth();
  
  // --- STATE ---
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(''); 
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Editable Email State
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);

  // --- PRINTING ---
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: assessment ? `LuxerAssess_Report_${assessment.candidate_email}` : 'Assessment_Report',
  });

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchResult = async () => {
      if (!token) return;
      
      if (!linkToken) {
          setError("Invalid link.");
          setLoading(false);
          return;
      }

      try {
        setLoading(true);
        const headers = { 'Authorization': `Bearer ${token}` };
        const response = await axios.get(`${API_URL}/${linkToken}`, { headers });
        setAssessment(response.data);
        
        // Initialize editable email with DB value
        setRecipientEmail(response.data.candidate_email || ''); 

      } catch (err) {
        console.error("Error fetching assessment result:", err);
        setError("Could not load results. You may not have permission.");
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [linkToken, token]);

  // --- METRIC CALCULATIONS ---
  const getTotalScore = () => {
    if (!assessment) return { score: 0, max: 0 };
    let score = 0;
    let max = 0;
    assessment.questions.forEach(q => {
      if (q.answer) { score += q.answer.score || 0; }
      max += 5; 
    });
    return { score, max };
  };

  const getSessionStats = () => {
    if (!assessment || !assessment.questions || assessment.questions.length === 0) {
        return { m: 0, s: 0, focusLoss: 0 };
    }
    const answer = assessment.questions[0].answer || {};
    const totalSeconds = answer.time_taken_seconds || 0;
    return {
        m: Math.floor(totalSeconds / 60),
        s: totalSeconds % 60,
        focusLoss: answer.focus_loss_count || 0
    };
  };

  const getFitMetrics = () => {
    if (!assessment) return { percent: 0, color: '#ccc', label: 'N/A' };
    const { score, max } = getTotalScore();
    const percent = max > 0 ? Math.round((score / max) * 100) : 0;
    let color = '#d32f2f', label = 'Low Fit';
    if (percent >= 80) { color = '#2e7d32'; label = 'High Fit'; }
    else if (percent >= 60) { color = '#f9a825'; label = 'Moderate Fit'; }
    return { percent, color, label };
  };

  const getIntegrityMetrics = () => {
    if (!assessment || !assessment.questions) return { label: 'Unknown', color: '#ccc', time: '0m 0s', pastes: 0 };
    const { m, s, focusLoss } = getSessionStats();
    let totalPastes = 0;
    assessment.questions.forEach(q => { if (q.answer) totalPastes += q.answer.paste_count || 0; });

    let label = 'High Confidence', color = '#2e7d32';
    if (focusLoss >= 4) { label = 'Low Confidence'; color = '#d32f2f'; }
    else if (focusLoss > 0 || totalPastes > 2) { label = 'Moderate Confidence'; color = '#f9a825'; }

    return { label, color, focusLoss, totalPastes, time: `${m}m ${s}s` };
  };

  const fit = getFitMetrics();
  const integrity = getIntegrityMetrics();
  const candidateLink = assessment ? `${window.location.origin}/take/${assessment.link_token}` : '';

  // --- HANDLERS ---
  const handleCopyLink = () => {
    navigator.clipboard.writeText(candidateLink);
    setCopySuccess('Copied!');
    setTimeout(() => setCopySuccess(''), 2000); 
  };

  // Save Email to DB
  const handleSaveEmail = async () => {
    if (!recipientEmail) return;
    setIsSavingEmail(true);
    try {
        // --- 3. Dynamic URL ---
        await axios.put(
            `${API_BASE_URL}/assessment/${assessment.id}/update-email`,
            { candidate_email: recipientEmail },
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert("Candidate email updated successfully!");
    } catch (err) {
        console.error("Update failed", err);
        alert("Failed to save new email.");
    } finally {
        setIsSavingEmail(false);
    }
  };

  // Send Invite
  const handleSendEmail = async () => {
    if (!recipientEmail) {
        alert("Please enter a candidate email.");
        return;
    }
    setEmailLoading(true);
    try {
        const payload = {
            candidate_email: recipientEmail, 
            job_title: assessment.job_title || "Candidate",
            link: candidateLink
        };
        // --- 4. Dynamic URL ---
        await axios.post(`${API_BASE_URL}/send-invite`, payload, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 5000); 
    } catch (err) {
        console.error("Email failed", err);
        alert("Failed to send email.");
    } finally {
        setEmailLoading(false);
    }
  };

  const renderIntegrityBadges = (answer) => {
    if (!answer || answer.paste_count === 0) return null;
    return <div className="integrity-badges"><span className="integrity-badge warning">📋 Paste Detected ({answer.paste_count})</span></div>;
  };

  // --- RENDER ---
  if (loading) return <div className="App"><Navbar /><header className="App-header"><h2 className="loading-text">Loading Results...</h2></header></div>;
  if (error) return <div className="App"><Navbar /><header className="App-header"><h2 className="error-message">{error}</h2></header></div>;
  if (!assessment) return <div className="App"><Navbar /><header className="App-header"><h2 className="loading-text">Assessment not found.</h2></header></div>;

  return (
    <div className="App">
      <Navbar />
      <header className="App-header" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <div className="form-container" style={{maxWidth: '1000px', width: '95%'}}>
          <Link to="/dashboard" className="back-link">{"<"} Back to Dashboard</Link>
          
          {/* TOP ACTION BAR */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
              <h2>Assessment Results</h2>
              <button onClick={() => handlePrint()} className="secondary-button">🖨️ Export PDF</button>
          </div>
          
          {/* SHAREABLE LINK SECTION */}
          <div className="share-link-section" style={{backgroundColor: '#eef2f7', padding: '15px', borderRadius: '6px', border: '1px solid #d1d9e6', marginBottom: '20px', textAlign: 'left'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                <span style={{fontSize: '0.9rem', fontWeight: 'bold', color: '#444'}}>Candidate Link (Secure):</span>
                {copySuccess && <span style={{fontSize: '0.8rem', color: '#2e7d32', fontWeight: 'bold'}}>{copySuccess}</span>}
            </div>
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                <input type="text" readOnly value={candidateLink} style={{flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', minWidth: '200px'}} />
                <button onClick={handleCopyLink} className="secondary-button" style={{marginTop: 0, padding: '0 15px', height: 'auto'}}>Copy</button>
                <button onClick={handleSendEmail} className="generate-button" disabled={emailLoading || emailSent} style={{marginTop: 0, padding: '0 20px', height: 'auto', width: 'auto'}}>
                    {emailLoading ? 'Sending...' : emailSent ? '✅ Sent!' : '📧 Send Invite'}
                </button>
                <a href={candidateLink} target="_blank" rel="noopener noreferrer" className="secondary-button" style={{marginTop: 0, padding: '8px 15px', height: 'auto', textDecoration: 'none', display: 'flex', alignItems: 'center'}}>Open</a>
            </div>
          </div>

          {/* --- PRINTABLE AREA --- */}
          <div ref={componentRef} style={{padding: '10px'}}>
            
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px'}}>
                <div style={{flex: 1, minWidth: '200px'}}> 
                    <h3 style={{margin: 0}}>{assessment.job_title}</h3>
                    
                    {/* --- 5. MOBILE FIX RE-APPLIED HERE --- */}
                    <div style={{marginTop: '5px', color: '#666', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                        <span>Candidate Email:</span> 
                        <input 
                            type="email"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            style={{
                                border: 'none', 
                                borderBottom: '1px dashed #ccc', 
                                padding: '2px 5px', 
                                fontSize: '1rem', 
                                color: '#333', 
                                fontWeight: 'bold', 
                                backgroundColor: 'transparent',
                                minWidth: '150px', // Allow shrinking on mobile
                                maxWidth: '100%'   // Prevent overflow
                            }}
                        />
                        <button onClick={handleSaveEmail} disabled={isSavingEmail} style={{fontSize: '0.8rem', padding: '4px 8px', cursor: 'pointer', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', color: '#0d47a1', borderRadius: '4px'}}>
                            {isSavingEmail ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                    {/* ------------------------------------ */}

                </div>
                <span className={`status-badge status-${assessment.status.toLowerCase()}`} style={{fontSize: '0.9rem', padding: '6px 12px'}}>
                    {assessment.status}
                </span>
            </div>

            <div className="executive-dashboard-grid">
                {/* FIT SCORE */}
                <div className="metric-card">
                    <h4 style={{margin: '0 0 15px 0', color: '#444', display: 'flex', alignItems: 'center'}}>
                        Luxer Fit Score 
                        <Tooltip text="Aggregate of all AI grading scores (0-5) converted to a percentage. Represents technical alignment with the generated rubric." />
                    </h4>
                    <div className="fit-score-circle" style={{'--score-percent': `${fit.percent}%`, '--score-color': fit.color}}>
                        <div className="fit-score-inner">
                            <span className="fit-percentage" style={{color: fit.color}}>{fit.percent}%</span>
                            <span className="fit-label">{fit.label}</span>
                        </div>
                    </div>
                </div>

                {/* INTEGRITY */}
                <div className="metric-card" style={{alignItems: 'flex-start', padding: '25px'}}>
                    <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                        <h4 style={{margin: '0 0 15px 0', color: '#444', display: 'flex', alignItems: 'center'}}>
                            Session Integrity
                            <Tooltip text="Automated proctoring data. 'Confidence' drops if the candidate switches tabs frequently or pastes large blocks of text." />
                        </h4>
                    </div>
                    <div className="integrity-row">
                        <span className="integrity-label">Confidence Level</span>
                        <span className="integrity-value" style={{color: integrity.color}}>{integrity.label}</span>
                    </div>
                    <div className="integrity-row">
                        <span className="integrity-label">Exited Window</span>
                        <span className="integrity-value">{integrity.focusLoss}x</span>
                    </div>
                    <div className="integrity-row">
                        <span className="integrity-label">External Aid (Pastes)</span>
                        <span className="integrity-value">{integrity.totalPastes} detected</span>
                    </div>
                    <div className="integrity-row">
                        <span className="integrity-label">Total Duration</span>
                        <span className="integrity-value">{integrity.time}</span>
                    </div>
                    <div style={{marginTop: '15px', fontSize: '0.75rem', color: '#888', fontStyle: 'italic', lineHeight: '1.4'}}>
                        * Integrity metrics are behavioral indicators, not proof of misconduct.
                    </div>
                </div>
            </div>

            <div className="executive-summary">
                <h3 style={{marginTop: 0, color: '#0052cc'}}>Executive Summary</h3>
                <p style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>{assessment.critical_analysis}</p>
            </div>

            <h3>Question Breakdown</h3>
            {assessment.questions.map((q, index) => (
                <div key={q.id} className="result-card question-card" style={{pageBreakInside: 'avoid'}}>
                <h4>Question {index + 1}</h4>
                <p className="question-text">{q.question_text}</p>
                <div className="competency-badge">Competency: {q.competency_tested}</div>

                <details style={{marginTop: '10px', marginBottom: '10px', color: '#555', fontSize: '0.9rem'}}>
                    <summary style={{cursor: 'pointer', fontWeight: '600', color: '#0052cc'}}>View Target Rubric (Criteria for 5/5)</summary>
                    <div style={{marginTop: '10px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '4px', borderLeft: '3px solid #0052cc', whiteSpace: 'pre-wrap', fontStyle: 'italic'}}>
                        {q.grading_rubric}
                    </div>
                </details>

                <div className="answer-section">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: '5px'}}>
                        <h5 style={{margin: 0}}>Candidate's Answer:</h5>
                        {renderIntegrityBadges(q.answer)}
                    </div>
                    <div className="answer-box">
                        {q.answer ? q.answer.answer_text : "No answer provided."}
                    </div>
                </div>

                <div className="grade-section">
                    <h5>AI Grade: <span className="score-badge">{q.answer ? q.answer.score : "N/A"} / 5</span></h5>
                    <p className="reasoning-text">{q.answer ? q.answer.reasoning : "N/A"}</p>
                </div>
                </div>
            ))}
          </div>
          
          <div style={{marginTop: '20px'}}>
             <Link to="/dashboard" className="nav-link-button">Go to Dashboard</Link>
          </div>
        </div>
      </header>

      <Footer />

    </div>
  );
}

export default AssessmentResultPage;